import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';
import {RxFormBuilder, RxwebValidators} from '@rxweb/reactive-form-validators';
import {forkJoin} from 'rxjs';
import {FileUpload} from 'primeng/fileupload';
import {SelectItem} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {plainToInstance} from 'class-transformer';
import {SupportRequestRestService} from '../../api/services/support-request.rest.service';
import {SupportRequestAttachmentRestService} from '../../api/services/support-request-attachment.rest.service';
import {SupportRequestApiModel} from '../../api/models/support-request.api-model';
import {SupportRequestAttachmentApiModel} from '../../api/models/support-request-attachment.api-model';
import {ResponseNotificationService} from '../../core/services/response-notification.service';
import {SupportRequestCategoryEnum} from '../../core/enums/support-request-category.enum';

@Component({selector: 'app-support-request-form', templateUrl: './support-request-form.component.html', styleUrls: ['./support-request-form.component.scss']})
export class SupportRequestFormComponent implements OnInit, OnDestroy {
  @Input() public allowAttachments = false;
  @Output() public submitted = new EventEmitter<SupportRequestApiModel>();
  @ViewChild('uploader') public fileUploader?: FileUpload;
  public form: UntypedFormGroup;
  public attachments: {file: File; previewUrl: string | null}[] = [];
  public uploadProgress: number | null = null;
  public categories: SelectItem[] = [];

  constructor(private readonly fb: RxFormBuilder, private readonly translate: TranslateService, private readonly supportRequestRS: SupportRequestRestService, private readonly attachmentRS: SupportRequestAttachmentRestService, private readonly rns: ResponseNotificationService) {}

  public ngOnInit(): void {
    this.categories = Object.values(SupportRequestCategoryEnum).map(value => ({value, label: this.translate.instant(`features.support.categories.${value.toLowerCase()}`)}));
    this.form = this.fb.group({
      category: [null, [RxwebValidators.required()]],
      contactEmail: [null, [RxwebValidators.required(), RxwebValidators.maxLength({value: 255}), RxwebValidators.email()]],
      topic: [null, [RxwebValidators.required(), RxwebValidators.minLength({value: 3}), RxwebValidators.maxLength({value: 255})]],
      content: [null, [RxwebValidators.required(), RxwebValidators.minLength({value: 3})]],
    });
  }

  public ngOnDestroy(): void { this.revokePreviewUrls(); }
  public onFileSelect(event: {files?: File[]}): void {
    const files = Array.from(event?.files ?? []);
    const nextFiles = [...this.attachments, ...files.map(file => ({file, previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null}))];
    if (nextFiles.length > 10) { this.rns.emitError(this.translate.instant('features.support.too_many_files')); this.fileUploader?.clear(); return; }
    this.attachments = nextFiles;
    this.fileUploader?.clear();
  }
  public removeAttachment(index: number): void { const attachment = this.attachments[index]; if (attachment?.previewUrl) URL.revokeObjectURL(attachment.previewUrl); this.attachments = this.attachments.filter((_, i) => i !== index); }
  public submit(): void {
    if (!this.form.valid) { this.form.markAllAsTouched(); return; }
    if (!this.attachments.length) { this.createRequest([]); return; }
    this.uploadProgress = 0;
    forkJoin(this.attachments.map(attachment => this.attachmentRS.uploadWithProgress(attachment.file))).subscribe({
      next: results => this.createRequest(results.filter((result): result is SupportRequestAttachmentApiModel => typeof result !== 'number')),
      error: () => { this.uploadProgress = null; this.rns.emitError(this.translate.instant('features.support.error_upload')); }
    });
  }
  private createRequest(attachments: SupportRequestAttachmentApiModel[]): void {
    const request = plainToInstance(SupportRequestApiModel, this.form.value);
    request.attachments = attachments.map(attachment => attachment['@id']);
    this.supportRequestRS.create(request).subscribe(created => {
      this.rns.emitMessage(this.translate.instant('features.support.success'));
      this.submitted.emit(created);
      this.form.reset();
      this.revokePreviewUrls();
      this.attachments = [];
      this.uploadProgress = null;
    });
  }
  private revokePreviewUrls(): void { this.attachments.forEach(attachment => attachment.previewUrl && URL.revokeObjectURL(attachment.previewUrl)); }
}
